import {Phone, Calendar, Shield, Sparkles, Car, Award, Check, Zap, MapPin} from 'lucide-react'
import { useNavigate } from "react-router-dom"
import { useEffect } from "react"
import CanonicalTag from '@/components/CanonicalTag'
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'

const TeslaPpfSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    // Add FAQ Schema
    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Why do Tesla owners need PPF more than other vehicles?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Tesla's water-based paint is thinner and more susceptible to rock chips and scratches than traditional automotive paint. PPF provides critical protection for maintaining appearance and resale value, especially in San Diego's coastal and highway driving environments."
          }
        },
        {
          "@type": "Question",
          "name": "How long does Tesla PPF installation take?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front coverage takes 1 day. Full front coverage typically requires 2 days. Full vehicle PPF installation takes 3-5 days depending on the model and coverage areas."
          }
        },
        {
          "@type": "Question",
          "name": "Will PPF affect Tesla's Autopilot or sensors?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. We properly mask and protect all sensors, cameras, and Autopilot hardware during installation. PPF is optically clear and does not interfere with any Tesla technology or safety systems."
          }
        },
        {
          "@type": "Question",
          "name": "How much does Tesla PPF cost in San Diego?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Partial front starts at $1,299, full front starts at $1,899, and full vehicle starts at $4,499. Pricing varies by Tesla model and coverage options. Larger vehicles and specialty films may cost more."
          }
        },
        {
          "@type": "Question",
          "name": "Does PPF void Tesla warranty?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "No. Paint protection film is a cosmetic aftermarket addition that does not void Tesla's factory warranty when professionally installed."
          }
        },
        {
          "@type": "Question",
          "name": "Can I combine Tesla PPF with chrome delete or window tint?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Many Tesla owners combine PPF with chrome delete, ceramic window tint, and caliper painting for comprehensive protection and customization in one visit."
          }
        }
      ]
    })
    document.head.appendChild(script)

    // Set meta tags
    document.title = "Tesla PPF San Diego | Professional Paint Protection Film for Tesla Vehicles"
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert Tesla PPF installation in San Diego. Protect Model S, 3, X, Y, Cybertruck with professional paint protection film. Experienced installers, self-healing technology, manufacturer-backed film warranties. Serving Vista, Carlsbad, Oceanside.')
    }

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/tesla-ppf-san-diego" />
      <div className="pt-20 min-h-screen bg-black">
      {/* Hero */}
      <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Tesla PPF San Diego - Professional Paint Protection Film for Tesla Vehicles</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
              Protect your Tesla investment with professional paint protection film installation in San Diego. Our installers specialize in PPF for Model S, Model 3, Model X, Model Y, and Cybertruck—delivering careful coverage, self-healing technology, and long-term paint preservation.
            </p>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Serving Tesla owners throughout San Diego County including Vista, Carlsbad, Oceanside, Encinitas, and Rancho Santa Fe. We understand Tesla's unique paint chemistry and apply premium self-healing film using bulk or precision-pattern methods selected for each Tesla model and panel.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Phone size={18} />
                858-282-8119
              </a>
              <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                <Calendar size={18} />
                Get Free Tesla PPF Quote
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Tesla Owners Choose PPF */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Tesla Owners in San Diego Choose PPF</h2>
          <div className="max-w-4xl mx-auto space-y-6 text-gray-300 text-lg">
            <p>
              Tesla vehicles feature advanced paint technology and distinctive finishes that require specialized protection. San Diego's coastal environment, highway debris, and year-round sun exposure make <a href="/paint-protection-film" className="text-cyan-400 hover:underline">paint protection film</a> essential for maintaining your Tesla's showroom appearance and resale value.
            </p>
            <p>
              Our Tesla PPF installations use bulk film or precision-cut patterns selected for each Tesla model—Model S, Model 3, Model X, Model Y, and Cybertruck. Edges are wrapped where appropriate so coverage stays complete on the front bumper, hood, fenders, mirrors, door edges, rocker panels, and rear impact zones.
            </p>
            <p>
              Tesla's thin, water-based paint is more susceptible to rock chips, scratches, and road debris than traditional automotive paint. PPF creates an invisible barrier with self-healing properties that repair minor scratches and swirl marks with heat, maintaining a flawless finish for years. Many Tesla owners in <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a>, <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a>, and throughout North County San Diego protect their vehicles immediately after delivery to preserve factory paint from day one.
            </p>
            <p>
              We also offer <a href="/chrome-delete" className="text-cyan-400 hover:underline">chrome delete</a> services to complement your Tesla PPF installation, creating a seamless blacked-out aesthetic popular among Model 3 and Model Y owners. Combined with <a href="/window-tinting" className="text-cyan-400 hover:underline">ceramic window tint</a>, your Tesla receives comprehensive protection and enhanced appearance in one visit to our Vista facility.
            </p>
          </div>
        </div>
      </section>

      {/* Tesla PPF Coverage Options & Pricing */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla PPF Pricing & Coverage Packages</h2>
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Partial Front Coverage</h3>
              <p className="text-gray-300 mb-6">
                Essential protection for high-impact zones: front bumper, partial hood, mirrors, and headlights. Ideal for daily drivers and highway commuters protecting the most vulnerable areas.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,299</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by Tesla model</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Front bumper full coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Partial hood (12-18 inches)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Mirrors and headlights</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Self-healing technology</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border-2 border-cyan-500">
              <div className="bg-cyan-500 text-black text-xs font-bold px-3 py-1 rounded-full inline-block mb-4">MOST POPULAR</div>
              <h3 className="text-2xl font-bold mb-4 text-white">Full Front Coverage</h3>
              <p className="text-gray-300 mb-6">
                Comprehensive front-end protection: full hood, bumper, fenders, mirrors, headlights, and fog lights. The most popular choice for Tesla owners seeking maximum highway protection.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $1,899</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by Tesla model</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All partial coverage areas included</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Full hood, fenders, and bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Headlights, fog lights, mirrors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Manufacturer-backed film warranty</span>
                </li>
              </ul>
            </div>
            <div className="bg-black p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Full Vehicle Coverage</h3>
              <p className="text-gray-300 mb-6">
                Ultimate protection for your entire Tesla: complete vehicle wrap with PPF covering every painted surface. Maximum resale value protection and a permanently pristine finish.
              </p>
              <div className="text-cyan-400 text-3xl font-bold mb-2">Starting at $4,499</div>
              <p className="text-gray-400 text-sm mb-6">Pricing varies by Tesla model</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>All full front coverage areas</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Doors, rocker panels, quarters</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Roof, trunk, rear bumper</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={18} />
                  <span>Lifetime paint preservation</span>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm">
            All packages include professional paint preparation, precision installation, and manufacturer warranty. Starting prices shown above are based on Tesla model and size. Exact pricing will be determined after a free consultation and vehicle inspection.
          </p>
        </div>
      </section>

      {/* Tesla-Specific Benefits */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla-Specific PPF Installation Expertise</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Zap className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Model-Specific Patterns</h3>
              <p className="text-gray-300">Bulk film or precision-cut patterns are selected for Model S, 3, X, Y, and Cybertruck so coverage stays clean without unnecessary trimming on your paint.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Shield className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Paint Chemistry Knowledge</h3>
              <p className="text-gray-300">We understand Tesla's water-based paint and apply installation techniques that prevent edge lifting and adhesion issues.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Sensor & Trim Expertise</h3>
              <p className="text-gray-300">Proper disassembly and reinstallation of Tesla sensors, cameras, trim pieces, and emblems for seamless full-coverage results.</p>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <div className="bg-cyan-500/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Award className="text-cyan-400" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white">Professional Installers</h3>
              <p className="text-gray-300">Experienced installation team specializing in Tesla vehicles, with careful technique and manufacturer warranty documentation on the film selected.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Our Tesla PPF Installation Process</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">1</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Free Consultation & Tesla Inspection</h3>
                <p className="text-gray-300">We inspect your Tesla's paint condition, discuss coverage options specific to your model, and provide a detailed quote based on your protection goals. We'll review your daily driving patterns, parking situation, and budget to recommend the ideal PPF package.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">2</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Paint Preparation & Correction</h3>
                <p className="text-gray-300">We thoroughly wash and decontaminate your Tesla using pH-balanced automotive shampoo and clay treatment. For used Teslas, we perform paint correction to remove existing swirl marks, water spots, and light scratches, ensuring a flawless surface for PPF adhesion.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">3</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Precision Installation, Selected by Model and Panel</h3>
                <p className="text-gray-300">Using model-specific templates designed for your exact Tesla variant, we apply premium self-healing PPF with professional wet installation techniques. When necessary, we properly disassemble trim pieces, sensors, and badges to achieve complete edge-to-edge coverage without visible lines or gaps.</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="bg-cyan-500 text-black font-bold w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl">4</div>
              <div>
                <h3 className="text-2xl font-bold mb-3 text-white">Quality Inspection & Delivery</h3>
                <p className="text-gray-300">Final multi-point inspection ensures perfect edges, no bubbles, complete adhesion, and proper sensor/camera function. We provide comprehensive care instructions, warranty documentation, and aftercare guidelines before delivering your protected Tesla.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Tesla Models */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla Models We Protect in San Diego</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Tesla Model 3</h3>
              <p className="text-gray-300 mb-4">San Diego's most popular Tesla model. We install PPF on thousands of Model 3s annually, protecting the front fascia, rocker panels, and full vehicle with precision-cut patterns that follow every curve and contour.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Front bumper stone chip protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Rocker panel coverage for low clearance</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Chrome delete compatibility</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Tesla Model Y</h3>
              <p className="text-gray-300 mb-4">Perfect for families and outdoor enthusiasts. Model Y PPF protects against off-road debris, parking lot scratches, and highway rock chips across all painted surfaces including rear bumper loading areas.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Rear bumper loading area protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Door edge and handle coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Full roof and pillar options</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Tesla Model S</h3>
              <p className="text-gray-300 mb-4">Luxury sedan requiring expert installation. We protect Model S paint with full-front or full-vehicle PPF, maintaining elegance and resale value for years to come.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Large hood and fender coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Gloss enhancement for deep colors</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Premium film for luxury finish</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Tesla Model X</h3>
              <p className="text-gray-300 mb-4">SUV protection for family adventures. Model X benefits from PPF on all high-impact zones, falcon door edges, and rear liftgate areas prone to wear from cargo loading.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Falcon door edge protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Rear hatch and bumper coverage</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Rocker and side panel protection</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Tesla Cybertruck</h3>
              <p className="text-gray-300 mb-4">Specialized protection for stainless steel surfaces. We apply PPF to Cybertruck exterior panels, preventing fingerprints, stains, and oxidation while maintaining the unique brushed finish.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Stainless steel surface protection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Fingerprint and stain prevention</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Custom coverage patterns</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Custom Coverage</h3>
              <p className="text-gray-300 mb-4">Need specific protection zones? We design custom Tesla PPF packages targeting your unique concerns—door handles, charging ports, rear arches, or any vulnerable area.</p>
              <ul className="space-y-2 text-gray-300 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>À la carte coverage options</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Problem area targeting</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-cyan-400 mt-1 flex-shrink-0" size={16} />
                  <span>Budget-friendly solutions</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-white text-center">Tesla PPF FAQs - San Diego</h2>
          <div className="space-y-6">
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Why do Tesla owners need PPF more than other vehicles?</h3>
              <p className="text-gray-300">Tesla's water-based paint is thinner and more susceptible to rock chips and scratches than traditional automotive paint. PPF provides critical protection for maintaining appearance and resale value, especially in San Diego's coastal and highway driving environments where salt air, UV rays, and freeway debris constantly threaten your paint finish.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can PPF be applied to a used Tesla?</h3>
              <p className="text-gray-300">Yes. We perform paint correction to remove existing swirl marks and light scratches before PPF installation, restoring a flawless finish. PPF then prevents future damage and preserves the corrected paint. Many used Tesla buyers protect their investment immediately after purchase.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How long does Tesla PPF installation take?</h3>
              <p className="text-gray-300">Partial front coverage takes 1 day. Full front coverage typically requires 2 days. Full vehicle PPF installation takes 3-5 days depending on the model and coverage areas. We never rush quality work—proper curing time ensures long-lasting adhesion and bubble-free results.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Will PPF affect Tesla's Autopilot or sensors?</h3>
              <p className="text-gray-300">No. We properly mask and protect all sensors, cameras, and Autopilot hardware during installation. PPF is optically clear and does not interfere with any Tesla technology, safety systems, or driver assistance features. Radar and ultrasonic sensors function normally through the film.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">How much does Tesla PPF cost in San Diego?</h3>
              <p className="text-gray-300">Partial front coverage starts at $1,299. Full front coverage starts at $1,899. Full vehicle PPF starts at $4,499. Final pricing depends on your Tesla model, paint condition, and coverage selection — larger vehicles and specialty films may cost more. We provide transparent quotes with no hidden fees.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Does PPF void Tesla warranty?</h3>
              <p className="text-gray-300">No. Paint protection film is a cosmetic aftermarket addition that does not void Tesla's factory warranty. Professional installation ensures no damage to paint or vehicle systems. In fact, PPF helps maintain warranty-compliant paint condition by preventing damage.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">Can I combine Tesla PPF with other services?</h3>
              <p className="text-gray-300">Absolutely. Many Tesla owners combine PPF with <a href="/chrome-delete" className="text-cyan-400 hover:underline">chrome delete</a>, <a href="/window-tinting" className="text-cyan-400 hover:underline">ceramic window tint</a>, and <a href="/caliper-painting" className="text-cyan-400 hover:underline">caliper painting</a> for comprehensive protection and customization in one visit. We offer package discounts for combined services.</p>
            </div>
            <div className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
              <h3 className="text-xl font-bold mb-3 text-white">What's the warranty on Tesla PPF installation?</h3>
              <p className="text-gray-300">Premium PPF includes a 10-year manufacturer warranty against yellowing, cracking, peeling, and delamination. Our installation workmanship is also guaranteed—we'll fix any bubbling, lifting, or adhesion issues at no charge during the warranty period.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Tesla Models We Protect - Dedicated Model Pages */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-4 text-white text-center">Tesla Models We Protect</h2>
          <p className="text-gray-300 text-center max-w-3xl mx-auto mb-12">
            Explore our dedicated paint protection film pages for each Tesla model. Every guide covers model-specific coverage zones, pricing, and installation details for Tesla owners across San Diego County.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <a href="/tesla-model-y-ppf-san-diego" className="bg-black p-8 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group flex flex-col">
              <div className="bg-cyan-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Tesla Model Y PPF</h3>
              <p className="text-gray-400 text-sm mb-6 flex-grow">PPF protection for San Diego's favorite electric SUV—shielding the front fascia, rear loading area, and door edges from daily wear and road debris.</p>
              <span className="text-cyan-400 text-sm font-semibold inline-flex items-center gap-1">View Model Y PPF →</span>
            </a>
            <a href="/tesla-model-3-ppf-san-diego" className="bg-black p-8 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group flex flex-col">
              <div className="bg-cyan-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Tesla Model 3 PPF</h3>
              <p className="text-gray-400 text-sm mb-6 flex-grow">Precision-cut PPF for the Model 3, protecting the front bumper, rocker panels, and full body with chrome delete compatibility for a clean look.</p>
              <span className="text-cyan-400 text-sm font-semibold inline-flex items-center gap-1">View Model 3 PPF →</span>
            </a>
            <a href="/tesla-model-s-ppf-san-diego" className="bg-black p-8 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group flex flex-col">
              <div className="bg-cyan-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Tesla Model S PPF</h3>
              <p className="text-gray-400 text-sm mb-6 flex-grow">Expert full-front and full-vehicle PPF for the luxury Model S sedan, preserving deep paint finishes, elegance, and long-term resale value.</p>
              <span className="text-cyan-400 text-sm font-semibold inline-flex items-center gap-1">View Model S PPF →</span>
            </a>
            <a href="/tesla-model-x-ppf-san-diego" className="bg-black p-8 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group flex flex-col">
              <div className="bg-cyan-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-6 border border-cyan-500">
                <Car className="text-cyan-400" size={28} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-cyan-400 transition">Tesla Model X PPF</h3>
              <p className="text-gray-400 text-sm mb-6 flex-grow">Durable PPF coverage for the Model X SUV, protecting falcon door edges, rear liftgate, and high-impact zones from family-adventure wear.</p>
              <span className="text-cyan-400 text-sm font-semibold inline-flex items-center gap-1">View Model X PPF →</span>
            </a>
          </div>
        </div>
      </section>

      {/* Embedded Quote Form */}
      <section className="py-16 bg-gray-950">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <EmbeddedQuoteForm
            defaultService="ppf"
            heading="Get a Free Tesla PPF Quote"
            subheading="Tell us your Tesla model, year, and coverage goals. We'll respond with an honest, transparent estimate — no pressure, no obligation."
          />
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
          <p className="text-gray-300 text-center mb-8">
            Enhance your Tesla protection with our complementary services:
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <a href="/paint-protection-film-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film San Diego</h3>
              <p className="text-gray-400 text-sm">Professional PPF for all vehicle makes and models with self-healing technology and manufacturer-backed film warranties</p>
            </a>
            <a href="/colored-ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Colored PPF San Diego</h3>
              <p className="text-gray-400 text-sm">Color change paint protection film in gloss, satin, matte, and stealth finishes with full self-healing protection</p>
            </a>
            <a href="/paint-protection-film" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">Paint Protection Film Services</h3>
              <p className="text-gray-400 text-sm">Complete PPF installation guide, coverage options, and technology overview for all vehicles</p>
            </a>
            <a href="/ppf-san-diego" className="bg-black p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition">San Diego PPF Installation</h3>
              <p className="text-gray-400 text-sm">Local paint protection film installation serving Vista, Carlsbad, Oceanside, and North County</p>
            </a>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
            <MapPin className="text-cyan-400" size={32} />
            Tesla PPF Installation Serving All of San Diego County
          </h2>
          <p className="text-gray-300 text-lg mb-6 text-center">
            Located in <a href="/ppf-vista" className="text-cyan-400 hover:underline font-semibold">Vista, California</a>, we provide professional Tesla paint protection film services to owners throughout North County San Diego and beyond.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-gray-300">
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">North County San Diego</h3>
              <ul className="space-y-1 text-sm">
                <li>• <a href="/ppf-vista" className="text-cyan-400 hover:underline">Vista</a> (Shop Location)</li>
                <li>• <a href="/ppf-carlsbad" className="text-cyan-400 hover:underline">Carlsbad</a></li>
                <li>• <a href="/ppf-oceanside" className="text-cyan-400 hover:underline">Oceanside</a></li>
                <li>• <a href="/ppf-encinitas" className="text-cyan-400 hover:underline">Encinitas</a></li>
                <li>• <a href="/ppf-rancho-santa-fe" className="text-cyan-400 hover:underline">Rancho Santa Fe</a></li>
                <li>• San Marcos</li>
                <li>• Escondido</li>
              </ul>
            </div>
            <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
              <h3 className="font-bold text-white mb-2">Greater San Diego Area</h3>
              <ul className="space-y-1 text-sm">
                <li>• <a href="/ppf-san-diego" className="text-cyan-400 hover:underline">San Diego</a></li>
                <li>• La Jolla</li>
                <li>• Del Mar</li>
                <li>• Solana Beach</li>
                <li>• Poway</li>
                <li>• Ramona</li>
                <li>• All San Diego County</li>
              </ul>
            </div>
          </div>
          <p className="text-gray-300 text-center mt-8">
            <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Protect Your Tesla with Professional PPF Installation</h2>
          <p className="text-xl text-white/90 mb-8">
            Schedule your free Tesla PPF consultation and get a custom quote for your Model S, 3, X, Y, or Cybertruck. Same-week appointments available.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => { navigate("/contact"); setTimeout(() => window.scrollTo(0, 0), 100); }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Calendar size={20} />
              Get Free Tesla PPF Quote
            </button>
            <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
              <Phone size={20} />
              Call: 858-282-8119
            </a>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default TeslaPpfSanDiegoPage
